import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported as isAnalyticsSupported, logEvent as fbLogEvent } from "firebase/analytics";
import { getDatabase, ref, set, get, push, update, remove, onValue, serverTimestamp } from "firebase/database";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as firebaseSignOut, updateProfile } from "firebase/auth";
import { getStorage } from "firebase/storage";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: "x10-ace08.firebaseapp.com",
  databaseURL: "https://x10-ace08-default-rtdb.firebaseio.com",
  projectId: "x10-ace08",
  storageBucket: "x10-ace08.firebasestorage.app",
  messagingSenderId: "79124200698",
  appId: "1:79124200698:web:8f0ad6171f98974c989d43",
  measurementId: "G-B12GDLNNVL"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Analytics is best-effort: it requires reachable firebaseinstallations.googleapis.com.
// Ad blockers, offline state, or restricted networks make it fail. The internal
// logEvent kicks off an unawaited promise chain, so its rejection escapes any try/catch
// here and surfaces as an uncaught runtime error. To prevent that, we (1) only init
// when isSupported() AND the browser is online, and (2) attach a global handler that
// silences only the firebase installations rejection.
let analytics = null;
let analyticsReady = null;

const initAnalytics = async () => {
  if (analytics) return analytics;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
  try {
    const supported = await isAnalyticsSupported();
    if (!supported) return null;
    analytics = getAnalytics(app);
    return analytics;
  } catch (err) {
    console.warn('Firebase Analytics unavailable:', err?.message || err);
    return null;
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const msg = event?.reason?.message || '';
    if (msg.includes('installations/app-offline') || msg.includes('Installations:')) {
      event.preventDefault();
    }
  });
  analyticsReady = initAnalytics();
}

const logEvent = (eventName, params) => {
  const fire = (a) => {
    if (!a) return;
    try { fbLogEvent(a, eventName, params); } catch (err) {
      console.warn('logEvent failed:', err?.message || err);
    }
  };
  if (analytics) return fire(analytics);
  if (analyticsReady) analyticsReady.then(fire).catch(() => {});
};

// Export initialized services
export const database = getDatabase(app);
export const auth = getAuth(app);
export const storage = getStorage(app);

// Export analytics for use in other components
export { analytics, logEvent };

// Auth helpers
const googleProvider = new GoogleAuthProvider();

export const authAPI = {
  signInWithGoogle: async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Google sign-in failed:', error);
      return { success: false, error: error.message };
    }
  },
  signInWithEmail: async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Email sign-in failed:', error);
      return { success: false, error: error.message };
    }
  },
  registerWithEmail: async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Email registration failed:', error);
      return { success: false, error: error.message };
    }
  },
  signOut: async () => {
    try {
      console.log('Firebase signOut called, auth state:', auth.currentUser);
      await firebaseSignOut(auth);
      console.log('Firebase signOut completed successfully');
      return { success: true };
    } catch (error) {
      console.error('Sign-out failed:', error);
      return { success: false, error: error.message };
    }
  },
};

// Database Helper Functions with Best Practices

// Goals Management
export const goalsAPI = {
  // Create a new goal
  createGoal: async (userId, goalData) => {
    try {
      const goalRef = push(ref(database, `users/${userId}/goals`));
      const goalId = goalRef.key;
      
      const goal = {
        id: goalId,
        title: goalData.goal,
        description: goalData.description,
        status: 'pending', // 'pending', 'in_progress', 'completed', 'archived'
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        targetDate: goalData.targetDate || null,
        priority: goalData.priority || 'medium',
        category: goalData.category || 'personal',
        progress: 0,
        metadata: {
          daysWhenSet: goalData.daysWhenSet || 0,
          estimatedDuration: goalData.estimatedDuration || null
        }
      };
      
      await set(goalRef, goal);
      return { success: true, goalId, goal };
    } catch (error) {
      console.error('Error creating goal:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all goals for a user
  getUserGoals: async (userId) => {
    try {
      console.log('Firebase: getUserGoals called for userId:', userId);
      const goalsRef = ref(database, `users/${userId}/goals`);
      console.log('Firebase: Getting data from path:', `users/${userId}/goals`);
      
      const snapshot = await get(goalsRef);
      console.log('Firebase: Snapshot exists:', snapshot.exists());
      
      if (snapshot.exists()) {
        const goalsData = snapshot.val();
        console.log('Firebase: Raw goals data:', goalsData);
        console.log('Firebase: Raw goals data type:', typeof goalsData);
        console.log('Firebase: Raw goals data keys:', Object.keys(goalsData));
        
        const goals = Object.keys(goalsData).map(key => ({
          id: key,
          ...goalsData[key]
        }));
        console.log('Firebase: Processed goals:', goals);
        console.log('Firebase: Number of goals:', goals.length);
        
        return { success: true, goals };
      }
      
      console.log('Firebase: No goals found, returning empty array');
      return { success: true, goals: [] };
    } catch (error) {
      console.error('Firebase: Error fetching goals:', error);
      return { success: false, error: error.message };
    }
  },

  // Update goal status and progress
  updateGoal: async (userId, goalId, updates) => {
    try {
      const goalRef = ref(database, `users/${userId}/goals/${goalId}`);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      await update(goalRef, updateData);
      return { success: true };
    } catch (error) {
      console.error('Error updating goal:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a goal
  deleteGoal: async (userId, goalId) => {
    try {
      console.log('Starting comprehensive goal deletion for goalId:', goalId);
      
      // 1. Delete the goal itself
      const goalRef = ref(database, `users/${userId}/goals/${goalId}`);
      await remove(goalRef);
      console.log('Goal deleted');
      
      // 2. Delete all business plans for this goal
      const businessPlansRef = ref(database, `users/${userId}/businessPlans`);
      const businessPlansSnapshot = await get(businessPlansRef);
      if (businessPlansSnapshot.exists()) {
        const businessPlans = businessPlansSnapshot.val();
        for (const [planId, plan] of Object.entries(businessPlans)) {
          if (plan.goalId === goalId) {
            await remove(ref(database, `users/${userId}/businessPlans/${planId}`));
            console.log('Deleted business plan:', planId);
          }
        }
      }
      
      // 3. Delete all conversations for this goal
      const conversationsRef = ref(database, `users/${userId}/conversations`);
      const conversationsSnapshot = await get(conversationsRef);
      if (conversationsSnapshot.exists()) {
        const conversations = conversationsSnapshot.val();
        for (const [convId, conversation] of Object.entries(conversations)) {
          if (conversation.goalId === goalId) {
            await remove(ref(database, `users/${userId}/conversations/${convId}`));
            console.log('Deleted conversation:', convId);
          }
        }
      }
      
      // 4. Delete all progress data for this goal (goal-specific storage)
      const goalProgressRef = ref(database, `users/${userId}/progress/${goalId}`);
      await remove(goalProgressRef);
      console.log('Deleted all progress data for goal:', goalId);
      
      // Also clean up any legacy progress data that might still have goalId references
      const legacyProgressRef = ref(database, `users/${userId}/progress/tasks`);
      const legacySnapshot = await get(legacyProgressRef);
      if (legacySnapshot.exists()) {
        const tasks = legacySnapshot.val();
        for (const [taskId, task] of Object.entries(tasks)) {
          if (task.goalId === goalId) {
            await remove(ref(database, `users/${userId}/progress/tasks/${taskId}`));
            console.log('Cleaned up legacy progress task:', taskId);
          }
        }
      }
      
      // Clean up daily history for this goal
      const goalDailyHistoryRef = ref(database, `users/${userId}/dailyHistory/${goalId}`);
      await remove(goalDailyHistoryRef);
      console.log('Deleted daily history for goal:', goalId);
      
      // 5. Delete any vision board data for this goal
      const visionBoardRef = ref(database, `users/${userId}/visionBoards/${goalId}`);
      const visionBoardSnapshot = await get(visionBoardRef);
      if (visionBoardSnapshot.exists()) {
        await remove(visionBoardRef);
        console.log('Deleted vision board for goal');
      }
      
      console.log('Goal and all related data deleted successfully');
      return { success: true };
    } catch (error) {
      console.error('Error deleting goal and related data:', error);
      return { success: false, error: error.message };
    }
  }
};

// Conversations Management
export const conversationsAPI = {
  // Create a new conversation
  createConversation: async (userId, goalId, conversationData) => {
    try {
      const conversationRef = push(ref(database, `users/${userId}/conversations`));
      const conversationId = conversationRef.key;
      
      const conversation = {
        id: conversationId,
        goalId: goalId,
        type: conversationData.type || '11_question_assessment',
        status: 'active', // 'active', 'completed', 'archived'
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        messages: [],
        metadata: {
          totalQuestions: conversationData.totalQuestions || 11,
          currentQuestion: 0,
          completedQuestions: 0
        }
      };
      
      await set(conversationRef, conversation);
      return { success: true, conversationId, conversation };
    } catch (error) {
      console.error('Error creating conversation:', error);
      return { success: false, error: error.message };
    }
  },

  // Add message to conversation
  addMessage: async (userId, conversationId, messageData) => {
    try {
      const messageRef = push(ref(database, `users/${userId}/conversations/${conversationId}/messages`));
      const messageId = messageRef.key;
      
      const message = {
        id: messageId,
        type: messageData.type, // 'user', 'assistant'
        content: messageData.content,
        timestamp: serverTimestamp(),
        metadata: messageData.metadata || {}
      };
      
      await set(messageRef, message);
      
      // Update conversation metadata
      const conversationRef = ref(database, `users/${userId}/conversations/${conversationId}`);
      await update(conversationRef, {
        updatedAt: serverTimestamp(),
        [`metadata/currentQuestion`]: messageData.questionNumber || 0
      });
      
      return { success: true, messageId, message };
    } catch (error) {
      console.error('Error adding message:', error);
      return { success: false, error: error.message };
    }
  },

  // Get conversation with messages
  getConversation: async (userId, conversationId) => {
    try {
      const conversationRef = ref(database, `users/${userId}/conversations/${conversationId}`);
      const snapshot = await get(conversationRef);
      
      if (snapshot.exists()) {
        const conversationData = snapshot.val();
        const messages = conversationData.messages ? 
          Object.keys(conversationData.messages).map(key => ({
            id: key,
            ...conversationData.messages[key]
          })).sort((a, b) => a.timestamp - b.timestamp) : [];
        
        return { 
          success: true, 
          conversation: {
            ...conversationData,
            messages
          }
        };
      }
      
      return { success: false, error: 'Conversation not found' };
    } catch (error) {
      console.error('Error fetching conversation:', error);
      return { success: false, error: error.message };
    }
  },

  // Complete conversation
  completeConversation: async (userId, conversationId) => {
    try {
      const conversationRef = ref(database, `users/${userId}/conversations/${conversationId}`);
      await update(conversationRef, {
        status: 'completed',
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error completing conversation:', error);
      return { success: false, error: error.message };
    }
  }
};

// Business Plans Management
export const businessPlansAPI = {
  // Create a business plan
  createBusinessPlan: async (userId, goalId, conversationId, planData) => {
    try {
      const planRef = push(ref(database, `users/${userId}/businessPlans`));
      const planId = planRef.key;
      
      const businessPlan = {
        id: planId,
        goalId: goalId,
        conversationId: conversationId,
        title: planData.goal || 'Business Plan',
        description: planData.description || 'AI-generated business plan',
        content: planData.content || '',
        status: 'active', // 'active', 'completed', 'archived'
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        phases: planData.phases || [],
        progress: {
          currentPhase: 0,
          completedTasks: 0,
          totalTasks: 0,
          overallProgress: 0
        },
        metadata: {
          type: planData.type || 'business_plan',
          aiGenerated: true,
          version: '1.0'
        }
      };
      
      await set(planRef, businessPlan);
      
      // Update goal status to in_progress
      await goalsAPI.updateGoal(userId, goalId, {
        status: 'in_progress',
        businessPlanId: planId
      });
      
      return { success: true, planId, businessPlan };
    } catch (error) {
      console.error('Error creating business plan:', error);
      return { success: false, error: error.message };
    }
  },

  // Get business plan
  getBusinessPlan: async (userId, planId) => {
    try {
      const planRef = ref(database, `users/${userId}/businessPlans/${planId}`);
      const snapshot = await get(planRef);
      
      if (snapshot.exists()) {
        return { success: true, businessPlan: snapshot.val() };
      }
      
      return { success: false, error: 'Business plan not found' };
    } catch (error) {
      console.error('Error fetching business plan:', error);
      return { success: false, error: error.message };
    }
  },

  // Update business plan progress
  updateProgress: async (userId, planId, progressData) => {
    try {
      const planRef = ref(database, `users/${userId}/businessPlans/${planId}`);
      await update(planRef, {
        [`progress`]: progressData,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Get business plans for a goal
  getBusinessPlansForGoal: async (userId, goalId) => {
    try {
      const plansRef = ref(database, `users/${userId}/businessPlans`);
      const snapshot = await get(plansRef);
      
      if (snapshot.exists()) {
        const plansData = snapshot.val();
        const plans = Object.keys(plansData)
          .map(key => ({ id: key, ...plansData[key] }))
          .filter(plan => plan.goalId === goalId);
        
        return { success: true, businessPlans: plans };
      }
      
      return { success: true, businessPlans: [] };
    } catch (error) {
      console.error('Error fetching business plans:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all user business plans
  getUserBusinessPlans: async (userId) => {
    try {
      const plansRef = ref(database, `users/${userId}/businessPlans`);
      const snapshot = await get(plansRef);
      
      if (snapshot.exists()) {
        const plansData = snapshot.val();
        const plans = Object.keys(plansData).map(id => ({
          id,
          ...plansData[id]
        })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        return { success: true, plans };
      }
      
      return { success: true, plans: [] };
    } catch (error) {
      console.error('Error getting user business plans:', error);
      return { success: false, error: error.message };
    }
  },

  // Update business plan
  updateBusinessPlan: async (userId, planId, updates) => {
    try {
      const planRef = ref(database, `users/${userId}/businessPlans/${planId}`);
      await update(planRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating business plan:', error);
      return { success: false, error: error.message };
    }
  }
};

// Progress Tracking API - Comprehensive Version
export const progressAPI = {
  // Record task completion
  recordTaskCompletion: async (userId, goalId, taskData) => {
    try {
      const path = goalId ? `users/${userId}/progress/${goalId}/tasks` : `users/${userId}/progress/tasks`;
      const taskRef = push(ref(database, path));
      const taskId = taskRef.key;
      
      const nowTs = Date.now();
      const task = {
        id: taskId,
        goalId: goalId || null,
        businessPlanId: taskData.businessPlanId || null,
        description: taskData.description,
        completed: taskData.completed || false,
        completedAt: taskData.completed ? serverTimestamp() : null,
        createdAt: nowTs, // store ms for easy client renders
        createdAtServer: serverTimestamp(),
        metadata: { ...(taskData.metadata || {}), createdAtMs: nowTs }
      };
      
      await set(taskRef, task);
      return { success: true, taskId, task };
    } catch (error) {
      console.error('Error recording task completion:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a recorded task (description, completed, metadata)
  updateTask: async (userId, taskId, updates) => {
    try {
      const taskRef = ref(database, `users/${userId}/progress/tasks/${taskId}`);
      const updateData = { ...updates, updatedAt: serverTimestamp() };
      await update(taskRef, updateData);
      return { success: true };
    } catch (error) {
      console.error('Error updating task:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a recorded task
  deleteTask: async (userId, taskId) => {
    try {
      const taskRef = ref(database, `users/${userId}/progress/tasks/${taskId}`);
      await remove(taskRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting task:', error);
      return { success: false, error: error.message };
    }
  },

  // Get progress for a goal
  getGoalProgress: async (userId, goalId) => {
    try {
      const tasksRef = ref(database, `users/${userId}/progress/tasks`);
      const snapshot = await get(tasksRef);
      
      if (snapshot.exists()) {
        const tasksData = snapshot.val();
        const goalTasks = Object.keys(tasksData)
          .map(key => ({ id: key, ...tasksData[key] }))
          .filter(task => task.goalId === goalId);
        
        const completedTasks = goalTasks.filter(task => task.completed).length;
        const totalTasks = goalTasks.length;
        const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
        
        return { 
          success: true, 
          progress: {
            completedTasks,
            totalTasks,
            progressPercentage,
            tasks: goalTasks
          }
        };
      }
      
      return { success: true, progress: { completedTasks: 0, totalTasks: 0, progressPercentage: 0, tasks: [] } };
    } catch (error) {
      console.error('Error fetching progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Get comprehensive progress data
  getProgressData: async (userId, businessPlanId) => {
    try {
      const progressRef = ref(database, `users/${userId}/progress/${businessPlanId}`);
      const snapshot = await get(progressRef);
      
      if (snapshot.exists()) {
        return { success: true, data: snapshot.val() };
      }
      
      return { success: true, data: {} };
    } catch (error) {
      console.error('Error fetching progress data:', error);
      return { success: false, error: error.message };
    }
  },

  // Update daily progress
  updateDailyProgress: async (userId, date, progressData) => {
    try {
      const dailyRef = ref(database, `users/${userId}/progress/daily/${date}`);
      await update(dailyRef, {
        ...progressData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating daily progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Update weekly progress
  updateWeeklyProgress: async (userId, weekStart, progressData) => {
    try {
      const weeklyRef = ref(database, `users/${userId}/progress/weekly/${weekStart}`);
      await update(weeklyRef, {
        ...progressData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating weekly progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Update monthly progress
  updateMonthlyProgress: async (userId, monthKey, progressData) => {
    try {
      const monthlyRef = ref(database, `users/${userId}/progress/monthly/${monthKey}`);
      await update(monthlyRef, {
        ...progressData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating monthly progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Update quarterly progress
  updateQuarterlyProgress: async (userId, quarterKey, progressData) => {
    try {
      const quarterlyRef = ref(database, `users/${userId}/progress/quarterly/${quarterKey}`);
      await update(quarterlyRef, {
        ...progressData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating quarterly progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Update time tracking
  updateTimeTracking: async (userId, date, timeData) => {
    try {
      const timeRef = push(ref(database, `users/${userId}/timeTracking/${date}`));
      await set(timeRef, {
        ...timeData,
        timestamp: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating time tracking:', error);
      return { success: false, error: error.message };
    }
  },


  // Get daily progress for a specific date
  getDailyProgress: async (userId, date) => {
    try {
      const dailyRef = ref(database, `users/${userId}/progress/tasks`);
      const snapshot = await get(dailyRef);
      
      if (snapshot.exists()) {
        const tasksData = snapshot.val();
        const dailyTasks = Object.keys(tasksData)
          .map(key => ({ id: key, ...tasksData[key] }))
          .filter(task => {
            const taskDate = task.metadata?.date || new Date(task.createdAt.seconds * 1000).toISOString().split('T')[0];
            return taskDate === date;
          });
        
        return { success: true, tasks: dailyTasks };
      }
      
      return { success: true, tasks: [] };
    } catch (error) {
      console.error('Error fetching daily progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Get weekly progress data
  getWeeklyProgress: async (userId) => {
    try {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
      
      const weeklyData = { totalTasks: 0, completedTasks: 0 };
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        const dateStr = date.toISOString().split('T')[0];
        
        const dailyData = await progressAPI.getDailyProgress(userId, dateStr);
        if (dailyData.success) {
          weeklyData.totalTasks += dailyData.tasks.length;
          weeklyData.completedTasks += dailyData.tasks.filter(task => task.completed).length;
        }
      }
      
      return { success: true, weeklyData };
    } catch (error) {
      console.error('Error fetching weekly progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Get time tracking for a specific date
  getTimeTracking: async (userId, date) => {
    try {
      const timeRef = ref(database, `users/${userId}/timeTracking/${date}`);
      const snapshot = await get(timeRef);
      
      if (snapshot.exists()) {
        const sessionsData = snapshot.val();
        const sessions = Object.keys(sessionsData).map(key => ({ id: key, ...sessionsData[key] }));
        return { success: true, sessions };
      }
      
      return { success: true, sessions: [] };
    } catch (error) {
      console.error('Error fetching time tracking:', error);
      return { success: false, error: error.message };
    }
  },

  // Record time session
  recordTimeSession: async (userId, sessionData) => {
    try {
      const date = sessionData.date || new Date().toISOString().split('T')[0];
      const sessionRef = push(ref(database, `users/${userId}/timeTracking/${date}`));
      
      const session = {
        id: sessionRef.key,
        goalId: sessionData.goalId,
        activity: sessionData.activity,
        duration: sessionData.duration, // in seconds
        timestamp: sessionData.timestamp || serverTimestamp(),
        createdAt: serverTimestamp()
      };
      
      await set(sessionRef, session);
      return { success: true, sessionId: sessionRef.key, session };
    } catch (error) {
      console.error('Error recording time session:', error);
      return { success: false, error: error.message };
    }
  },

  // Get streak data for a user goal
  getStreakData: async (userId, goalId) => {
    try {
      const tasksRef = ref(database, `users/${userId}/progress/tasks`);
      const snapshot = await get(tasksRef);
      
      if (snapshot.exists()) {
        const tasksData = snapshot.val();
        const goalTasks = Object.keys(tasksData)
          .map(key => ({ id: key, ...tasksData[key] }))
          .filter(task => task.goalId === goalId && task.completed);
        
        // Calculate streak by checking consecutive days with completed tasks
        const taskDates = goalTasks.map(task => {
          return task.metadata?.date || new Date(task.completedAt.seconds * 1000).toISOString().split('T')[0];
        });
        
        // Get unique dates and sort them
        const uniqueDates = [...new Set(taskDates)].sort();
        
        // Calculate current streak
        let streakDays = 0;
        const today = new Date().toISOString().split('T')[0];
        let checkDate = today;
        
        while (uniqueDates.includes(checkDate)) {
          streakDays++;
          const date = new Date(checkDate);
          date.setDate(date.getDate() - 1);
          checkDate = date.toISOString().split('T')[0];
        }
        
        return { success: true, streakDays };
      }
      
      return { success: true, streakDays: 0 };
    } catch (error) {
      console.error('Error calculating streak:', error);
      return { success: false, error: error.message };
    }
  },

  // Calculate and update aggregated metrics
  updateAggregatedMetrics: async (userId, businessPlanId) => {
    try {
      // This would calculate various metrics from the raw progress data
      const progressData = await progressAPI.getProgressData(userId, businessPlanId);
      
      if (progressData.success) {
        const metrics = calculateAggregatedMetrics(progressData.data);
        
        const metricsRef = ref(database, `users/${userId}/metrics/${businessPlanId}`);
        await set(metricsRef, {
          ...metrics,
          lastCalculated: serverTimestamp()
        });
        
        return { success: true, metrics };
      }
      
      return { success: false, error: 'Failed to fetch progress data' };
    } catch (error) {
      console.error('Error updating aggregated metrics:', error);
      return { success: false, error: error.message };
    }
  },

  // Get progress data for user
  getProgress: async (userId, businessPlanId) => {
    try {
      const progressRef = ref(database, `users/${userId}/progress`);
      const snapshot = await get(progressRef);
      
      if (snapshot.exists()) {
        return snapshot.val();
      } else {
        return { daily: {}, weekly: {}, monthly: {}, quarterly: {} };
      }
    } catch (error) {
      console.error('Error getting progress:', error);
      return { daily: {}, weekly: {}, monthly: {}, quarterly: {} };
    }
  },

};

// User Management
export const userAPI = {
  // Initialize user profile
  initializeUser: async (userId, userData) => {
    try {
      const userRef = ref(database, `users/${userId}/profile`);
      const profile = {
        id: userId,
        email: userData.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        onboardingCompleted: false,
        preferences: {
          theme: 'dark',
          notifications: true
        }
      };
      
      await set(userRef, profile);
      return { success: true, profile };
    } catch (error) {
      console.error('Error initializing user:', error);
      return { success: false, error: error.message };
    }
  },

  // Update user profile
  updateProfile: async (userId, updates) => {
    try {
      const userRef = ref(database, `users/${userId}/profile`);
      await update(userRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  }
};

// Real-time listeners
// Daily Reports API
export const reportsAPI = {
  // Save report text for a given date
  saveReport: async (userId, dateISO, text) => {
    try {
      const reportRef = ref(database, `users/${userId}/reports/${dateISO}`);
      await set(reportRef, { text, updatedAt: serverTimestamp() });
      return { success: true };
    } catch (error) {
      console.error('Error saving report:', error);
      return { success: false, error: error.message };
    }
  },
  // Load report
  getReport: async (userId, dateISO) => {
    try {
      const reportRef = ref(database, `users/${userId}/reports/${dateISO}`);
      const snapshot = await get(reportRef);
      if (snapshot.exists()) return { success: true, text: snapshot.val().text || '' };
      return { success: true, text: '' };
    } catch (error) {
      console.error('Error fetching report:', error);
      return { success: false, error: error.message };
    }
  }
};

export const createRealtimeListener = (path, callback) => {
  const dbRef = ref(database, path);
  return onValue(dbRef, callback);
};



// Helper function to calculate aggregated metrics
const calculateAggregatedMetrics = (progressData) => {
  // Calculate various performance metrics
  const metrics = {
    overall: {
      completionRate: 0,
      consistencyScore: 0,
      productivityTrend: 'stable',
      averageSessionTime: 0
    },
    daily: {
      averageTasksCompleted: 0,
      averageProductivity: 0,
      bestPerformanceDay: null
    },
    weekly: {
      averageWeeklyCompletion: 0,
      consistencyScore: 0,
      improvementRate: 0
    },
    monthly: {
      targetAchievementRate: 0,
      growthRate: 0,
      milestoneCompletion: 0
    },
    quarterly: {
      objectiveCompletion: 0,
      strategicAlignment: 0,
      adaptabilityScore: 0
    }
  };

  // Implement metric calculations based on progressData
  // This is a simplified version - you'd implement more sophisticated calculations
  
  return metrics;
};

// Todo Management API - AI Memory Integration
export const todosAPI = {
  // Create a new todo with AI memory context
  createTodo: async (userId, goalId, todoData) => {
    try {
      const todoRef = push(ref(database, `users/${userId}/todos`));
      const todoId = todoRef.key;
      
      const todo = {
        id: todoId,
        goalId: goalId || null, // Link to current goal context
        text: todoData.text,
        completed: false,
        priority: todoData.priority || 'medium',
        category: todoData.category || 'general',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        dueDate: todoData.dueDate || null,
        aiContext: {
          generatedByAI: todoData.aiGenerated || false,
          businessPlanPhase: todoData.businessPlanPhase || null,
          memoryBucket: 'daily_tasks', // For AI categorization
          relatedConversations: todoData.relatedConversations || []
        },
        metadata: {
          estimatedTime: todoData.estimatedTime || null,
          complexity: todoData.complexity || 'medium',
          energyRequired: todoData.energyRequired || 'medium',
          dateContext: new Date().toISOString().split('T')[0]
        }
      };
      
      await set(todoRef, todo);
      
      // Update daily progress context for AI memory
      await progressAPI.recordTaskCompletion(userId, goalId, {
        description: todoData.text,
        completed: false,
        businessPlanId: todoData.businessPlanId,
        metadata: {
          date: new Date().toISOString().split('T')[0],
          priority: todoData.priority,
          aiGenerated: todoData.aiGenerated || false
        }
      });
      
      return { success: true, todoId, todo };
    } catch (error) {
      console.error('Error creating todo:', error);
      return { success: false, error: error.message };
    }
  },

  // Get todos with AI memory context
  getUserTodos: async (userId, filters = {}) => {
    try {
      const todosRef = ref(database, `users/${userId}/todos`);
      const snapshot = await get(todosRef);
      
      if (snapshot.exists()) {
        const todosData = snapshot.val();
        let todos = Object.keys(todosData).map(key => ({
          id: key,
          ...todosData[key]
        }));
        
        // Apply filters for AI context
        if (filters.goalId) {
          todos = todos.filter(todo => todo.goalId === filters.goalId);
        }
        if (filters.completed !== undefined) {
          todos = todos.filter(todo => todo.completed === filters.completed);
        }
        if (filters.priority) {
          todos = todos.filter(todo => todo.priority === filters.priority);
        }
        if (filters.dateContext) {
          todos = todos.filter(todo => todo.metadata.dateContext === filters.dateContext);
        }
        
        // Sort by priority and creation date for AI optimization
        todos.sort((a, b) => {
          const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
          if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
            return priorityOrder[b.priority] - priorityOrder[a.priority];
          }
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        
        return { success: true, todos };
      }
      
      return { success: true, todos: [] };
    } catch (error) {
      console.error('Error fetching todos:', error);
      return { success: false, error: error.message };
    }
  },

  // Update todo with AI memory tracking
  updateTodo: async (userId, todoId, updates) => {
    try {
      const todoRef = ref(database, `users/${userId}/todos/${todoId}`);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      // If marking as completed, record in AI memory
      if (updates.completed === true) {
        updateData.completedAt = serverTimestamp();
        
        // Get current todo data for context
        const currentSnapshot = await get(todoRef);
        if (currentSnapshot.exists()) {
          const todoData = currentSnapshot.val();
          
          // Record completion in progress tracking for AI analysis
          await progressAPI.recordTaskCompletion(userId, todoData.goalId, {
            description: todoData.text,
            completed: true,
            businessPlanId: todoData.aiContext?.businessPlanPhase,
            metadata: {
              date: new Date().toISOString().split('T')[0],
              priority: todoData.priority,
              completionTime: new Date().toISOString(),
              aiGenerated: todoData.aiContext?.generatedByAI || false
            }
          });
        }
      }
      
      await update(todoRef, updateData);
      return { success: true };
    } catch (error) {
      console.error('Error updating todo:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete todo with AI memory cleanup
  deleteTodo: async (userId, todoId) => {
    try {
      const todoRef = ref(database, `users/${userId}/todos/${todoId}`);
      await remove(todoRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting todo:', error);
      return { success: false, error: error.message };
    }
  },

  // Get AI-contextualized todos for daily planning
  getTodosForAIPlanning: async (userId, goalId, date) => {
    try {
      const dateKey = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      
      // Get todos filtered by context
      const todosResult = await todosAPI.getUserTodos(userId, { 
        goalId, 
        completed: false,
        dateContext: dateKey 
      });
      
      if (!todosResult.success) return todosResult;
      
      // Get AI memory context for intelligent suggestions
      const progressResult = await progressAPI.getGoalProgress(userId, goalId);
      const _recentMemory = await conversationsAPI.getConversation(userId, 'recent'); // eslint-disable-line no-unused-vars -- Would need recent conversation ID
      
      return {
        success: true,
        todos: todosResult.todos,
        aiContext: {
          progress: progressResult.success ? progressResult.progress : null,
          hasActiveTodos: todosResult.todos.length > 0,
          priorityDistribution: {
            high: todosResult.todos.filter(t => t.priority === 'high').length,
            medium: todosResult.todos.filter(t => t.priority === 'medium').length,
            low: todosResult.todos.filter(t => t.priority === 'low').length
          }
        }
      };
    } catch (error) {
      console.error('Error getting AI planning todos:', error);
      return { success: false, error: error.message };
    }
  },

  // Bulk operations for AI-generated todo lists
  createBulkTodos: async (userId, goalId, todosList) => {
    try {
      const results = [];
      
      for (const todoData of todosList) {
        const result = await todosAPI.createTodo(userId, goalId, {
          ...todoData,
          aiGenerated: true,
          businessPlanPhase: todoData.phase || null
        });
        results.push(result);
      }
      
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      return {
        success: failed.length === 0,
        created: successful.length,
        failed: failed.length,
        results
      };
    } catch (error) {
      console.error('Error creating bulk todos:', error);
      return { success: false, error: error.message };
    }
  }
};

// Weekly Planning API - Hierarchical Goal Breakdown
export const weeklyPlanningAPI = {
  // Create weekly breakdown for a business plan
  createWeeklyBreakdown: async (userId, businessPlanId, weekData) => {
    try {
      const weekRef = push(ref(database, `users/${userId}/weeklyPlans/${businessPlanId}`));
      const weekId = weekRef.key;
      
      const weekPlan = {
        id: weekId,
        businessPlanId: businessPlanId,
        weekNumber: weekData.weekNumber,
        title: weekData.title,
        startDate: weekData.startDate,
        endDate: weekData.endDate,
        focus: weekData.focus,
        objectives: weekData.objectives || [],
        tasks: weekData.tasks || [],
        successCriteria: weekData.successCriteria || [],
        timeAllocation: weekData.timeAllocation || {
          deepWork: "40%",
          communication: "20%",
          learning: "25%",
          planning: "15%"
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active', // active, completed, archived
        progress: 0
      };
      
      await set(weekRef, weekPlan);
      return { success: true, weekId, weekPlan };
    } catch (error) {
      console.error('Error creating weekly breakdown:', error);
      return { success: false, error: error.message };
    }
  },

  // Get weekly plans for a business plan
  getWeeklyPlans: async (userId, businessPlanId) => {
    try {
      const weeksRef = ref(database, `users/${userId}/weeklyPlans/${businessPlanId}`);
      const snapshot = await get(weeksRef);
      
      if (snapshot.exists()) {
        const weeksData = snapshot.val();
        const weeks = Object.keys(weeksData).map(key => ({
          id: key,
          ...weeksData[key]
        })).sort((a, b) => a.weekNumber - b.weekNumber);
        
        return { success: true, weeks };
      }
      
      return { success: true, weeks: [] };
    } catch (error) {
      console.error('Error fetching weekly plans:', error);
      return { success: false, error: error.message };
    }
  },

  // Update weekly plan progress
  updateWeeklyPlan: async (userId, businessPlanId, weekId, updates) => {
    try {
      const weekRef = ref(database, `users/${userId}/weeklyPlans/${businessPlanId}/${weekId}`);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      await update(weekRef, updateData);
      return { success: true };
    } catch (error) {
      console.error('Error updating weekly plan:', error);
      return { success: false, error: error.message };
    }
  }
};

// Daily Actionable Goals API - Day-to-Day Execution
export const dailyGoalsAPI = {
  // Create daily actionable goals for a specific week
  createDailyGoals: async (userId, businessPlanId, weekId, dailyGoalsData) => {
    try {
      const dailyRef = push(ref(database, `users/${userId}/dailyGoals/${businessPlanId}/${weekId}`));
      const dailyId = dailyRef.key;
      
      const dailyGoals = {
        id: dailyId,
        businessPlanId: businessPlanId,
        weekId: weekId,
        date: dailyGoalsData.date,
        dayName: dailyGoalsData.dayName,
        focus: dailyGoalsData.focus,
        actionableGoals: dailyGoalsData.actionableGoals || [], // Array of specific actions
        timeBlocks: dailyGoalsData.timeBlocks || [], // Scheduled time blocks
        priorities: dailyGoalsData.priorities || ['high', 'medium', 'low'],
        estimatedHours: dailyGoalsData.estimatedHours || 8,
        energyLevel: dailyGoalsData.energyLevel || 'medium', // high, medium, low
        contextualNotes: dailyGoalsData.contextualNotes || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'pending', // pending, in_progress, completed
        progress: {
          completed: 0,
          total: dailyGoalsData.actionableGoals?.length || 0,
          percentage: 0
        },
        aiContext: {
          generatedByAI: dailyGoalsData.aiGenerated || false,
          businessPhase: dailyGoalsData.businessPhase || null,
          previousDayLearnings: dailyGoalsData.previousDayLearnings || null
        }
      };
      
      await set(dailyRef, dailyGoals);
      
      // Also create individual todos for each actionable goal
      if (dailyGoalsData.actionableGoals?.length > 0) {
        const todoPromises = dailyGoalsData.actionableGoals.map(goal => 
          todosAPI.createTodo(userId, dailyGoalsData.goalId || businessPlanId, {
            text: goal.description || goal,
            priority: goal.priority || 'medium',
            category: 'daily_goal',
            dueDate: dailyGoalsData.date,
            businessPlanPhase: weekId,
            businessPlanId: businessPlanId,
            aiGenerated: dailyGoalsData.aiGenerated || false,
            metadata: {
              dailyGoalId: dailyId,
              weekId: weekId,
              timeBlock: goal.timeBlock || null,
              estimatedTime: goal.estimatedTime || null
            }
          })
        );
        
        await Promise.all(todoPromises);
      }
      
      return { success: true, dailyId, dailyGoals };
    } catch (error) {
      console.error('Error creating daily goals:', error);
      return { success: false, error: error.message };
    }
  },

  // Get daily goals for a specific date or week
  getDailyGoals: async (userId, businessPlanId, filters = {}) => {
    try {
      const dailyRef = ref(database, `users/${userId}/dailyGoals/${businessPlanId}`);
      const snapshot = await get(dailyRef);
      
      if (snapshot.exists()) {
        const dailyData = snapshot.val();
        let dailyGoals = [];
        
        // Flatten the nested structure (businessPlan -> week -> daily)
        Object.keys(dailyData).forEach(weekId => {
          const weekData = dailyData[weekId];
          Object.keys(weekData).forEach(dailyId => {
            dailyGoals.push({
              id: dailyId,
              weekId: weekId,
              ...weekData[dailyId]
            });
          });
        });
        
        // Apply filters
        if (filters.date) {
          dailyGoals = dailyGoals.filter(goal => goal.date === filters.date);
        }
        if (filters.weekId) {
          dailyGoals = dailyGoals.filter(goal => goal.weekId === filters.weekId);
        }
        if (filters.status) {
          dailyGoals = dailyGoals.filter(goal => goal.status === filters.status);
        }
        
        // Sort by date
        dailyGoals.sort((a, b) => new Date(a.date) - new Date(b.date));
        
        return { success: true, dailyGoals };
      }
      
      return { success: true, dailyGoals: [] };
    } catch (error) {
      console.error('Error fetching daily goals:', error);
      return { success: false, error: error.message };
    }
  },

  // Update daily goal progress
  updateDailyGoalProgress: async (userId, businessPlanId, weekId, dailyId, progressUpdate) => {
    try {
      const dailyRef = ref(database, `users/${userId}/dailyGoals/${businessPlanId}/${weekId}/${dailyId}`);
      
      // Calculate new progress percentage
      const currentSnapshot = await get(dailyRef);
      if (currentSnapshot.exists()) {
        const currentData = currentSnapshot.val();
        const newCompleted = progressUpdate.completed || currentData.progress.completed;
        const total = currentData.progress.total;
        const percentage = total > 0 ? Math.round((newCompleted / total) * 100) : 0;
        
        const updateData = {
          progress: {
            completed: newCompleted,
            total: total,
            percentage: percentage
          },
          status: percentage === 100 ? 'completed' : (percentage > 0 ? 'in_progress' : 'pending'),
          updatedAt: serverTimestamp(),
          ...progressUpdate
        };
        
        await update(dailyRef, updateData);
        
        // Record in progress tracking for AI analysis
        await progressAPI.recordTaskCompletion(userId, businessPlanId, {
          description: `Daily goals for ${currentData.date}`,
          completed: percentage === 100,
          businessPlanId: businessPlanId,
          metadata: {
            date: currentData.date,
            dailyGoalId: dailyId,
            weekId: weekId,
            progressPercentage: percentage,
            completedGoals: newCompleted,
            totalGoals: total
          }
        });
        
        return { success: true, progress: updateData.progress };
      }
      
      return { success: false, error: 'Daily goal not found' };
    } catch (error) {
      console.error('Error updating daily goal progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Get today's actionable goals for AI planning
  getTodaysActionableGoals: async (userId, businessPlanId, date = new Date()) => {
    try {
      const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      
      const result = await dailyGoalsAPI.getDailyGoals(userId, businessPlanId, {
        date: dateStr
      });
      
      if (result.success && result.dailyGoals.length > 0) {
        const todayGoals = result.dailyGoals[0];
        
        // Also get related todos
        const todosResult = await todosAPI.getUserTodos(userId, {
          completed: false,
          dateContext: dateStr
        });
        
        return {
          success: true,
          dailyGoals: todayGoals,
          relatedTodos: todosResult.success ? todosResult.todos : [],
          aiContext: {
            hasPlannedGoals: true,
            totalActionableGoals: todayGoals.actionableGoals.length,
            estimatedHours: todayGoals.estimatedHours,
            focus: todayGoals.focus,
            energyLevel: todayGoals.energyLevel
          }
        };
      }
      
      return {
        success: true,
        dailyGoals: null,
        relatedTodos: [],
        aiContext: {
          hasPlannedGoals: false,
          needsPlanGeneration: true
        }
      };
    } catch (error) {
      console.error('Error getting today\'s actionable goals:', error);
      return { success: false, error: error.message };
    }
  }
};

// Hierarchical Planning API - Complete Goal-to-Daily Flow
export const hierarchicalPlanningAPI = {
  // Generate complete breakdown from goal to daily actions
  generateCompleteBreakdown: async (userId, goalId, businessPlanId) => {
    try {
      // Get business plan
      const businessPlanResult = await businessPlansAPI.getBusinessPlan(userId, businessPlanId);
      if (!businessPlanResult.success) {
        throw new Error('Business plan not found');
      }
      
      const businessPlan = businessPlanResult.businessPlan;
      
      // Generate weekly breakdown from business plan phases
      const weeklyPlans = [];
      let weekNumber = 1;
      
      for (const phase of businessPlan.phases) {
        // Each phase might span multiple weeks
        const phaseDuration = extractPhaseDuration(phase.dayRange);
        const weeksInPhase = Math.ceil(phaseDuration / 7);
        
        for (let w = 0; w < weeksInPhase; w++) {
          const weekStart = new Date();
          weekStart.setDate(weekStart.getDate() + (weekNumber - 1) * 7);
          
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);
          
          const weekData = {
            weekNumber: weekNumber,
            title: `Week ${weekNumber}: ${phase.title}`,
            startDate: weekStart.toISOString().split('T')[0],
            endDate: weekEnd.toISOString().split('T')[0],
            focus: phase.subtitle,
            objectives: phase.specificGoals,
            tasks: phase.weeklySchedule,
            successCriteria: [phase.successCriteria],
            timeAllocation: {
              deepWork: "40%",
              communication: "20%", 
              learning: "25%",
              planning: "15%"
            }
          };
          
          const weekResult = await weeklyPlanningAPI.createWeeklyBreakdown(
            userId, 
            businessPlanId, 
            weekData
          );
          
          if (weekResult.success) {
            weeklyPlans.push(weekResult.weekPlan);
            
            // Generate daily goals for this week
            await generateDailyGoalsForWeek(
              userId, 
              goalId,
              businessPlanId, 
              weekResult.weekId, 
              weekData,
              phase
            );
          }
          
          weekNumber++;
        }
      }
      
      return {
        success: true,
        businessPlanId: businessPlanId,
        weeklyPlans: weeklyPlans,
        totalWeeks: weekNumber - 1
      };
    } catch (error) {
      console.error('Error generating complete breakdown:', error);
      return { success: false, error: error.message };
    }
  },

  // Get complete hierarchy for a goal
  getGoalHierarchy: async (userId, goalId) => {
    try {
      // Get goal
      const goalResult = await goalsAPI.getUserGoals(userId);
      if (!goalResult.success) {
        throw new Error('Failed to fetch goals');
      }
      
      const goal = goalResult.goals.find(g => g.id === goalId);
      if (!goal) {
        throw new Error('Goal not found');
      }
      
      // Get business plans for this goal
      const businessPlansResult = await businessPlansAPI.getBusinessPlansForGoal(userId, goalId);
      if (!businessPlansResult.success) {
        throw new Error('Failed to fetch business plans');
      }
      
      const hierarchy = {
        goal: goal,
        businessPlans: []
      };
      
      // For each business plan, get weekly and daily breakdowns
      for (const businessPlan of businessPlansResult.businessPlans) {
        const weeklyResult = await weeklyPlanningAPI.getWeeklyPlans(userId, businessPlan.id);
        const planHierarchy = {
          ...businessPlan,
          weeks: []
        };
        
        if (weeklyResult.success) {
          for (const week of weeklyResult.weeks) {
            const dailyResult = await dailyGoalsAPI.getDailyGoals(userId, businessPlan.id, {
              weekId: week.id
            });
            
            planHierarchy.weeks.push({
              ...week,
              dailyGoals: dailyResult.success ? dailyResult.dailyGoals : []
            });
          }
        }
        
        hierarchy.businessPlans.push(planHierarchy);
      }
      
      return { success: true, hierarchy };
    } catch (error) {
      console.error('Error getting goal hierarchy:', error);
      return { success: false, error: error.message };
    }
  }
};

// Helper function to generate daily goals for a week
const generateDailyGoalsForWeek = async (userId, goalId, businessPlanId, weekId, weekData, phase) => {
  const startDate = new Date(weekData.startDate);
  const dailyGoalsPromises = [];
  
  for (let day = 0; day < 7; day++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + day);
    
    const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
    const dateStr = currentDate.toISOString().split('T')[0];
    
    // Generate actionable goals based on phase and day
    const actionableGoals = generateDailyActionsFromPhase(phase, dayName, day);
    
    const dailyGoalsData = {
      goalId: goalId,
      date: dateStr,
      dayName: dayName,
      focus: `${phase.title} - Day ${day + 1}`,
      actionableGoals: actionableGoals,
      timeBlocks: generateTimeBlocksFromRoutine(phase.dailyRoutine),
      estimatedHours: 8,
      energyLevel: getDayEnergyLevel(day),
      aiGenerated: true,
      businessPhase: phase.title
    };
    
    dailyGoalsPromises.push(
      dailyGoalsAPI.createDailyGoals(userId, businessPlanId, weekId, dailyGoalsData)
    );
  }
  
  await Promise.all(dailyGoalsPromises);
};

// Helper functions
const extractPhaseDuration = (dayRange) => {
  const match = dayRange.match(/(\d+)/);
  return match ? parseInt(match[1]) : 30; // Default to 30 days
};

const generateDailyActionsFromPhase = (phase, dayName, dayIndex) => {
  const baseActions = phase.specificGoals.map(goal => ({
    description: `Work on: ${goal}`,
    priority: 'high',
    estimatedTime: '2 hours'
  }));
  
  // Add day-specific actions based on weekly schedule
  if (phase.weeklySchedule && phase.weeklySchedule[dayIndex]) {
    baseActions.push({
      description: phase.weeklySchedule[dayIndex],
      priority: 'medium',
      estimatedTime: '1 hour'
    });
  }
  
  return baseActions;
};

const generateTimeBlocksFromRoutine = (routine) => {
  return [
    { time: '09:00', activity: routine.warmUp, priority: 'medium' },
    { time: '10:00', activity: routine.skillPractice, priority: 'high' },
    { time: '14:00', activity: routine.conditioning, priority: 'high' },
    { time: '16:00', activity: routine.coolDown, priority: 'low' }
  ];
};

const getDayEnergyLevel = (dayIndex) => {
  // Monday, Tuesday = high energy, Wednesday, Thursday = medium, Friday-Sunday = varied
  if (dayIndex <= 1) return 'high';
  if (dayIndex <= 3) return 'medium';
  return dayIndex === 4 ? 'medium' : 'low';
};

// Intent Classification & Training API - NLP Support
export const intentClassificationAPI = {
  // Add training sample for intent classification
  addTrainingSample: async (intentName, sampleData) => {
    try {
      const sampleRef = push(ref(database, `trainingSamples/${intentName}`));
      const sampleId = sampleRef.key;
      
      const sample = {
        id: sampleId,
        text: sampleData.text,
        confidence: sampleData.confidence || 1.0,
        userInput: sampleData.userInput,
        expectedIntent: intentName,
        features: sampleData.features || {}, // For ML feature extraction
        createdAt: serverTimestamp(),
        source: sampleData.source || 'manual', // 'manual', 'auto', 'validated'
        metadata: {
          userId: sampleData.userId || null,
          conversationId: sampleData.conversationId || null,
          validated: sampleData.validated || false
        }
      };
      
      await set(sampleRef, sample);
      return { success: true, sampleId, sample };
    } catch (error) {
      console.error('Error adding training sample:', error);
      return { success: false, error: error.message };
    }
  },

  // Get training samples for an intent
  getTrainingSamples: async (intentName, limit = 100) => {
    try {
      const samplesRef = ref(database, `trainingSamples/${intentName}`);
      const snapshot = await get(samplesRef);
      
      if (snapshot.exists()) {
        const samplesData = snapshot.val();
        let samples = Object.keys(samplesData).map(key => ({
          id: key,
          ...samplesData[key]
        }));
        
        // Sort by confidence and creation date
        samples.sort((a, b) => {
          if (a.confidence !== b.confidence) {
            return b.confidence - a.confidence;
          }
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        
        // Apply limit
        if (limit > 0) {
          samples = samples.slice(0, limit);
        }
        
        return { success: true, samples };
      }
      
      return { success: true, samples: [] };
    } catch (error) {
      console.error('Error fetching training samples:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all intent training data for ML model training
  getAllTrainingData: async () => {
    try {
      const trainingSamplesRef = ref(database, 'trainingSamples');
      const snapshot = await get(trainingSamplesRef);
      
      if (snapshot.exists()) {
        const trainingData = snapshot.val();
        const formattedData = [];
        
        Object.keys(trainingData).forEach(intentName => {
          const intentSamples = trainingData[intentName];
          Object.keys(intentSamples).forEach(sampleId => {
            formattedData.push({
              ...intentSamples[sampleId],
              intent: intentName
            });
          });
        });
        
        return { success: true, trainingData: formattedData };
      }
      
      return { success: true, trainingData: [] };
    } catch (error) {
      console.error('Error fetching all training data:', error);
      return { success: false, error: error.message };
    }
  },

  // Validate training sample (mark as confirmed correct)
  validateTrainingSample: async (intentName, sampleId, isCorrect) => {
    try {
      const sampleRef = ref(database, `trainingSamples/${intentName}/${sampleId}`);
      await update(sampleRef, {
        'metadata.validated': true,
        'metadata.isCorrect': isCorrect,
        'metadata.validatedAt': serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error validating training sample:', error);
      return { success: false, error: error.message };
    }
  },

  // Auto-generate training samples from conversation history
  generateTrainingSamplesFromConversations: async (userId, limit = 50) => {
    try {
      const conversationsRef = ref(database, `users/${userId}/conversations`);
      const snapshot = await get(conversationsRef);
      
      if (!snapshot.exists()) {
        return { success: true, generated: 0 };
      }
      
      const conversations = snapshot.val();
      let generated = 0;
      
      for (const [convId, conversation] of Object.entries(conversations)) {
        if (conversation.messages) {
          const messages = Object.values(conversation.messages);
          const userMessages = messages.filter(msg => msg.type === 'user');
          
          for (const message of userMessages) {
            if (generated >= limit) break;
            
            // Extract intent from message metadata or infer from content
            const intent = message.metadata?.intent || inferIntentFromContent(message.content);
            
            if (intent) {
              await intentClassificationAPI.addTrainingSample(intent, {
                text: message.content,
                userInput: message.content,
                confidence: message.metadata?.confidence || 0.8,
                source: 'auto',
                userId: userId,
                conversationId: convId,
                validated: false
              });
              generated++;
            }
          }
        }
        
        if (generated >= limit) break;
      }
      
      return { success: true, generated };
    } catch (error) {
      console.error('Error generating training samples:', error);
      return { success: false, error: error.message };
    }
  },

  // Get intent statistics for analytics
  getIntentStatistics: async () => {
    try {
      const trainingSamplesRef = ref(database, 'trainingSamples');
      const snapshot = await get(trainingSamplesRef);
      
      if (snapshot.exists()) {
        const trainingData = snapshot.val();
        const stats = {};
        
        Object.keys(trainingData).forEach(intentName => {
          const samples = Object.values(trainingData[intentName]);
          stats[intentName] = {
            totalSamples: samples.length,
            validatedSamples: samples.filter(s => s.metadata?.validated).length,
            averageConfidence: samples.reduce((sum, s) => sum + (s.confidence || 0), 0) / samples.length,
            sources: {
              manual: samples.filter(s => s.source === 'manual').length,
              auto: samples.filter(s => s.source === 'auto').length,
              validated: samples.filter(s => s.source === 'validated').length
            }
          };
        });
        
        return { success: true, statistics: stats };
      }
      
      return { success: true, statistics: {} };
    } catch (error) {
      console.error('Error fetching intent statistics:', error);
      return { success: false, error: error.message };
    }
  }
};

// AI Memory Management API - Memory Bucket Structure
export const aiMemoryAPI = {
  // Store long-term memory (persistent facts about user)
  storeLongTermMemory: async (userId, memoryData) => {
    try {
      const memoryRef = push(ref(database, `memory/${userId}/longTermMemory`));
      const memoryId = memoryRef.key;
      
      const memory = {
        id: memoryId,
        type: memoryData.type, // 'preference', 'fact', 'goal', 'skill', 'relationship'
        content: memoryData.content,
        context: memoryData.context || '',
        importance: memoryData.importance || 'medium', // 'low', 'medium', 'high', 'critical'
        confidence: memoryData.confidence || 0.8,
        source: memoryData.source || 'conversation', // 'conversation', 'profile', 'behavior'
        tags: memoryData.tags || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastAccessed: serverTimestamp(),
        accessCount: 0,
        relatedMemories: memoryData.relatedMemories || [],
        expiresAt: memoryData.expiresAt || null // For temporary long-term memories
      };
      
      await set(memoryRef, memory);
      return { success: true, memoryId, memory };
    } catch (error) {
      console.error('Error storing long-term memory:', error);
      return { success: false, error: error.message };
    }
  },

  // Store short-term memory (recent context, temporary facts)
  storeShortTermMemory: async (userId, memoryData) => {
    try {
      const memoryRef = push(ref(database, `memory/${userId}/shortTermMemory`));
      const memoryId = memoryRef.key;
      
      const memory = {
        id: memoryId,
        content: memoryData.content,
        context: memoryData.context,
        relevance: memoryData.relevance || 'medium',
        createdAt: serverTimestamp(),
        expiresAt: memoryData.expiresAt || Date.now() + (24 * 60 * 60 * 1000), // 24 hours default
        sessionId: memoryData.sessionId || null,
        conversationId: memoryData.conversationId || null,
        type: memoryData.type || 'context' // 'context', 'task', 'reminder', 'emotion'
      };
      
      await set(memoryRef, memory);
      
      // Auto-cleanup expired memories
      await aiMemoryAPI.cleanupExpiredMemories(userId, 'shortTermMemory');
      
      return { success: true, memoryId, memory };
    } catch (error) {
      console.error('Error storing short-term memory:', error);
      return { success: false, error: error.message };
    }
  },

  // Store session history (conversation flow, decisions made)
  storeSessionHistory: async (userId, sessionData) => {
    try {
      const sessionRef = push(ref(database, `memory/${userId}/sessionHistory`));
      const sessionId = sessionRef.key;
      
      const session = {
        id: sessionId,
        startTime: sessionData.startTime || serverTimestamp(),
        endTime: sessionData.endTime || null,
        interactions: sessionData.interactions || [],
        decisions: sessionData.decisions || [],
        context: sessionData.context || {},
        goalsFocused: sessionData.goalsFocused || [],
        tasksCompleted: sessionData.tasksCompleted || [],
        emotionalState: sessionData.emotionalState || 'neutral',
        productivityScore: sessionData.productivityScore || null,
        sessionType: sessionData.sessionType || 'general', // 'planning', 'execution', 'review'
        metadata: {
          platform: sessionData.platform || 'web',
          voiceUsed: sessionData.voiceUsed || false,
          duration: sessionData.duration || null
        }
      };
      
      await set(sessionRef, session);
      return { success: true, sessionId, session };
    } catch (error) {
      console.error('Error storing session history:', error);
      return { success: false, error: error.message };
    }
  },

  // Get relevant memories for AI context
  getRelevantMemories: async (userId, query, memoryTypes = ['longTermMemory', 'shortTermMemory']) => {
    try {
      const relevantMemories = {
        longTerm: [],
        shortTerm: [],
        totalRelevance: 0
      };
      
      for (const memoryType of memoryTypes) {
        const memoryRef = ref(database, `memory/${userId}/${memoryType}`);
        const snapshot = await get(memoryRef);
        
        if (snapshot.exists()) {
          const memories = Object.values(snapshot.val());
          
          // Simple relevance scoring based on keyword matching
          const scoredMemories = memories
            .filter(memory => {
              // Filter out expired memories
              if (memory.expiresAt && memory.expiresAt < Date.now()) {
                return false;
              }
              return true;
            })
            .map(memory => ({
              ...memory,
              relevanceScore: calculateMemoryRelevance(memory, query)
            }))
            .filter(memory => memory.relevanceScore > 0.2)
            .sort((a, b) => b.relevanceScore - a.relevanceScore);
          
          if (memoryType === 'longTermMemory') {
            relevantMemories.longTerm = scoredMemories.slice(0, 10);
          } else {
            relevantMemories.shortTerm = scoredMemories.slice(0, 5);
          }
        }
      }
      
      relevantMemories.totalRelevance = 
        relevantMemories.longTerm.length + relevantMemories.shortTerm.length;
      
      return { success: true, memories: relevantMemories };
    } catch (error) {
      console.error('Error getting relevant memories:', error);
      return { success: false, error: error.message };
    }
  },

  // Update memory access tracking
  accessMemory: async (userId, memoryType, memoryId) => {
    try {
      const memoryRef = ref(database, `memory/${userId}/${memoryType}/${memoryId}`);
      await update(memoryRef, {
        lastAccessed: serverTimestamp(),
        accessCount: database.ServerValue.increment(1)
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating memory access:', error);
      return { success: false, error: error.message };
    }
  },

  // Cleanup expired memories
  cleanupExpiredMemories: async (userId, memoryType) => {
    try {
      const memoryRef = ref(database, `memory/${userId}/${memoryType}`);
      const snapshot = await get(memoryRef);
      
      if (snapshot.exists()) {
        const memories = snapshot.val();
        const now = Date.now();
        let cleaned = 0;
        
        for (const [memoryId, memory] of Object.entries(memories)) {
          if (memory.expiresAt && memory.expiresAt < now) {
            await remove(ref(database, `memory/${userId}/${memoryType}/${memoryId}`));
            cleaned++;
          }
        }
        
        return { success: true, cleaned };
      }
      
      return { success: true, cleaned: 0 };
    } catch (error) {
      console.error('Error cleaning up memories:', error);
      return { success: false, error: error.message };
    }
  },

  // Get memory summary for AI system prompt
  getMemorySummary: async (userId) => {
    try {
      const [longTermResult, shortTermResult, sessionResult] = await Promise.all([
        get(ref(database, `memory/${userId}/longTermMemory`)),
        get(ref(database, `memory/${userId}/shortTermMemory`)),
        get(ref(database, `memory/${userId}/sessionHistory`))
      ]);
      
      const summary = {
        longTermFacts: [],
        recentContext: [],
        currentSession: null,
        userProfile: {}
      };
      
      // Process long-term memories
      if (longTermResult.exists()) {
        const longTermMemories = Object.values(longTermResult.val());
        summary.longTermFacts = longTermMemories
          .filter(m => m.importance === 'high' || m.importance === 'critical')
          .sort((a, b) => b.accessCount - a.accessCount)
          .slice(0, 10)
          .map(m => ({
            type: m.type,
            content: m.content,
            importance: m.importance
          }));
      }
      
      // Process short-term memories
      if (shortTermResult.exists()) {
        const shortTermMemories = Object.values(shortTermResult.val());
        const now = Date.now();
        summary.recentContext = shortTermMemories
          .filter(m => !m.expiresAt || m.expiresAt > now)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5)
          .map(m => ({
            content: m.content,
            context: m.context,
            type: m.type
          }));
      }
      
      // Get current session
      if (sessionResult.exists()) {
        const sessions = Object.values(sessionResult.val());
        const activeSessions = sessions.filter(s => !s.endTime);
        if (activeSessions.length > 0) {
          summary.currentSession = activeSessions[activeSessions.length - 1];
        }
      }
      
      return { success: true, summary };
    } catch (error) {
      console.error('Error getting memory summary:', error);
      return { success: false, error: error.message };
    }
  }
};

// Audio Response Storage API - TTS Integration
export const audioResponseAPI = {
  // Store audio response for conversation
  storeAudioResponse: async (userId, conversationId, audioData) => {
    try {
      const audioRef = push(ref(database, `audioResponses/${conversationId}`));
      const audioId = audioRef.key;
      
      const audioResponse = {
        id: audioId,
        conversationId: conversationId,
        userId: userId,
        audioUrl: audioData.audioUrl, // URL to stored audio file
        text: audioData.text, // Original text that was converted to speech
        voice: audioData.voice || 'default',
        duration: audioData.duration || null,
        format: audioData.format || 'mp3',
        ttsProvider: audioData.ttsProvider || 'browser',
        createdAt: serverTimestamp(),
        metadata: {
          speed: audioData.speed || 1.0,
          pitch: audioData.pitch || 1.0,
          volume: audioData.volume || 1.0,
          language: audioData.language || 'en-US'
        }
      };
      
      await set(audioRef, audioResponse);
      return { success: true, audioId, audioResponse };
    } catch (error) {
      console.error('Error storing audio response:', error);
      return { success: false, error: error.message };
    }
  },

  // Get audio responses for conversation
  getAudioResponses: async (conversationId) => {
    try {
      const audioRef = ref(database, `audioResponses/${conversationId}`);
      const snapshot = await get(audioRef);
      
      if (snapshot.exists()) {
        const audioData = snapshot.val();
        const audioResponses = Object.keys(audioData).map(key => ({
          id: key,
          ...audioData[key]
        })).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        
        return { success: true, audioResponses };
      }
      
      return { success: true, audioResponses: [] };
    } catch (error) {
      console.error('Error fetching audio responses:', error);
      return { success: false, error: error.message };
    }
  },

  // Update audio response with playback stats
  updateAudioStats: async (conversationId, audioId, stats) => {
    try {
      const audioRef = ref(database, `audioResponses/${conversationId}/${audioId}`);
      await update(audioRef, {
        [`stats.playCount`]: database.ServerValue.increment(1),
        [`stats.lastPlayed`]: serverTimestamp(),
        [`stats.totalPlayTime`]: stats.totalPlayTime || 0,
        [`stats.completionRate`]: stats.completionRate || 0
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating audio stats:', error);
      return { success: false, error: error.message };
    }
  }
};

// Event Logging API - System Debugging & Analytics
export const eventLoggingAPI = {
  // Log system event for debugging
  logEvent: async (eventData) => {
    try {
      const eventRef = push(ref(database, 'logs/systemEvents'));
      const eventId = eventRef.key;
      
      const event = {
        id: eventId,
        type: eventData.type, // 'error', 'warning', 'info', 'debug', 'user_action'
        message: eventData.message,
        details: eventData.details || {},
        userId: eventData.userId || null,
        sessionId: eventData.sessionId || null,
        timestamp: serverTimestamp(),
        source: eventData.source || 'unknown', // 'frontend', 'firebase', 'ai', 'voice'
        severity: eventData.severity || 'info', // 'critical', 'high', 'medium', 'low', 'info'
        metadata: {
          userAgent: eventData.userAgent || null,
          url: eventData.url || null,
          stack: eventData.stack || null,
          conversationId: eventData.conversationId || null
        }
      };
      
      await set(eventRef, event);
      
      // Also log to console for immediate debugging
      if (eventData.type === 'error') {
        console.error('🔴 Event logged:', event);
      } else if (eventData.type === 'warning') {
        console.warn('🟡 Event logged:', event);
      } else {
        console.log('🔵 Event logged:', event);
      }
      
      return { success: true, eventId, event };
    } catch (error) {
      console.error('Error logging event:', error);
      return { success: false, error: error.message };
    }
  },

  // Log user interaction for analytics
  logUserInteraction: async (userId, interactionData) => {
    try {
      const interactionRef = push(ref(database, `logs/userInteractions/${userId}`));
      const interactionId = interactionRef.key;
      
      const interaction = {
        id: interactionId,
        action: interactionData.action, // 'voice_command', 'button_click', 'goal_created', etc.
        component: interactionData.component || null,
        value: interactionData.value || null,
        timestamp: serverTimestamp(),
        sessionId: interactionData.sessionId || null,
        conversationId: interactionData.conversationId || null,
        duration: interactionData.duration || null,
        metadata: interactionData.metadata || {}
      };
      
      await set(interactionRef, interaction);
      return { success: true, interactionId, interaction };
    } catch (error) {
      console.error('Error logging user interaction:', error);
      return { success: false, error: error.message };
    }
  },

  // Log AI decision for debugging
  logAIDecision: async (userId, decisionData) => {
    try {
      const decisionRef = push(ref(database, `logs/aiDecisions/${userId}`));
      const decisionId = decisionRef.key;
      
      const decision = {
        id: decisionId,
        intent: decisionData.intent,
        confidence: decisionData.confidence,
        inputText: decisionData.inputText,
        response: decisionData.response,
        processingTime: decisionData.processingTime || null,
        timestamp: serverTimestamp(),
        conversationId: decisionData.conversationId || null,
        memoryUsed: decisionData.memoryUsed || [],
        alternatives: decisionData.alternatives || [],
        metadata: {
          model: decisionData.model || 'unknown',
          temperature: decisionData.temperature || null,
          tokens: decisionData.tokens || null
        }
      };
      
      await set(decisionRef, decision);
      return { success: true, decisionId, decision };
    } catch (error) {
      console.error('Error logging AI decision:', error);
      return { success: false, error: error.message };
    }
  },

  // Get recent logs for debugging
  getRecentLogs: async (logType = 'systemEvents', limit = 50) => {
    try {
      const logsRef = ref(database, `logs/${logType}`);
      const snapshot = await get(logsRef);
      
      if (snapshot.exists()) {
        const logsData = snapshot.val();
        const logs = Object.keys(logsData).map(key => ({
          id: key,
          ...logsData[key]
        }))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, limit);
        
        return { success: true, logs };
      }
      
      return { success: true, logs: [] };
    } catch (error) {
      console.error('Error fetching logs:', error);
      return { success: false, error: error.message };
    }
  },

  // Get error statistics
  getErrorStatistics: async (timeframe = '24h') => {
    try {
      const logsRef = ref(database, 'logs/systemEvents');
      const snapshot = await get(logsRef);
      
      if (snapshot.exists()) {
        const logs = Object.values(snapshot.val());
        const cutoffTime = Date.now() - getTimeframeMs(timeframe);
        
        const recentLogs = logs.filter(log => 
          new Date(log.timestamp).getTime() > cutoffTime
        );
        
        const stats = {
          totalEvents: recentLogs.length,
          errorCount: recentLogs.filter(log => log.type === 'error').length,
          warningCount: recentLogs.filter(log => log.type === 'warning').length,
          criticalCount: recentLogs.filter(log => log.severity === 'critical').length,
          topErrors: getTopErrors(recentLogs),
          timeframe: timeframe
        };
        
        return { success: true, statistics: stats };
      }
      
      return { success: true, statistics: { totalEvents: 0, errorCount: 0, warningCount: 0, criticalCount: 0, topErrors: [] } };
    } catch (error) {
      console.error('Error getting error statistics:', error);
      return { success: false, error: error.message };
    }
  }
};

// Helper functions for new APIs
const inferIntentFromContent = (content) => {
  const lowerContent = content.toLowerCase();
  
  if (lowerContent.match(/\b(goal|goals|target|achieve|plan|planning)\b/)) {
    return 'goal_planning';
  }
  if (lowerContent.match(/\b(task|todo|complete|done|finish)\b/)) {
    return 'task_management';
  }
  if (lowerContent.match(/\b(progress|status|how am i|doing|performance)\b/)) {
    return 'progress_check';
  }
  if (lowerContent.match(/\b(help|how|what|explain|tell me)\b/)) {
    return 'help_request';
  }
  
  return 'ai_chat';
};

const calculateMemoryRelevance = (memory, query) => {
  const queryLower = query.toLowerCase();
  const contentLower = memory.content.toLowerCase();
  
  let score = 0;
  
  // Exact phrase match
  if (contentLower.includes(queryLower)) {
    score += 1.0;
  }
  
  // Word overlap
  const queryWords = queryLower.split(' ');
  const contentWords = contentLower.split(' ');
  const overlap = queryWords.filter(word => contentWords.includes(word)).length;
  score += (overlap / queryWords.length) * 0.5;
  
  // Tag match
  if (memory.tags) {
    const tagMatch = memory.tags.some(tag => queryLower.includes(tag.toLowerCase()));
    if (tagMatch) score += 0.3;
  }
  
  // Importance boost
  if (memory.importance === 'critical') score *= 1.5;
  else if (memory.importance === 'high') score *= 1.2;
  
  // Recency boost for short-term memories
  if (memory.type && memory.createdAt) {
    const ageHours = (Date.now() - new Date(memory.createdAt).getTime()) / (1000 * 60 * 60);
    if (ageHours < 1) score *= 1.3;
    else if (ageHours < 6) score *= 1.1;
  }
  
  return Math.min(score, 1.0);
};

const getTimeframeMs = (timeframe) => {
  const timeframes = {
    '1h': 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000
  };
  return timeframes[timeframe] || timeframes['24h'];
};

const getTopErrors = (logs) => {
  const errorLogs = logs.filter(log => log.type === 'error');
  const errorCounts = {};

  errorLogs.forEach(log => {
    const key = log.message || 'Unknown error';
    errorCounts[key] = (errorCounts[key] || 0) + 1;
  });

  return Object.entries(errorCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([message, count]) => ({ message, count }));
};

// ==========================================
// Project Sharing API
// ==========================================

export const sharingAPI = {
  // Share a project with another user by email
  shareProject: async (ownerId, projectId, projectName, targetEmail, role = 'editor') => {
    try {
      // Find user by email
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);

      let targetUserId = null;
      if (snapshot.exists()) {
        const users = snapshot.val();
        for (const [uid, userData] of Object.entries(users)) {
          if (userData.profile?.email?.toLowerCase() === targetEmail.toLowerCase()) {
            targetUserId = uid;
            break;
          }
        }
      }

      // Get owner info
      const ownerSnapshot = await get(ref(database, `users/${ownerId}/profile`));
      const ownerProfile = ownerSnapshot.val() || {};

      // Create or update project share record
      const shareRef = ref(database, `projectShares/${projectId}`);
      const shareSnapshot = await get(shareRef);

      const shareData = shareSnapshot.exists() ? shareSnapshot.val() : {
        ownerId,
        ownerEmail: ownerProfile.email || '',
        ownerName: ownerProfile.name || '',
        projectName,
        createdAt: serverTimestamp(),
        members: {}
      };

      // Add pending invite or direct member
      if (targetUserId) {
        // User exists - add directly as member
        shareData.members[targetUserId] = {
          email: targetEmail,
          role,
          joinedAt: serverTimestamp(),
          status: 'active'
        };

        // Also add to user's sharedWithMe list
        await set(ref(database, `users/${targetUserId}/sharedWithMe/${projectId}`), {
          ownerId,
          ownerName: ownerProfile.name || '',
          projectName,
          role,
          sharedAt: serverTimestamp()
        });
      } else {
        // User doesn't exist yet - create pending invite
        const inviteCode = Math.random().toString(36).substring(2, 10);
        shareData.pendingInvites = shareData.pendingInvites || {};
        shareData.pendingInvites[inviteCode] = {
          email: targetEmail,
          role,
          createdAt: serverTimestamp(),
          expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000) // 7 days
        };
      }

      shareData.updatedAt = serverTimestamp();
      await set(shareRef, shareData);

      // Update the project's sharedWith field
      const projectShareRef = ref(database, `users/${ownerId}/projects/${projectId}/sharing`);
      await set(projectShareRef, {
        isShared: true,
        memberCount: Object.keys(shareData.members || {}).length,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        userFound: !!targetUserId,
        message: targetUserId
          ? `Project shared with ${targetEmail}`
          : `Invite sent to ${targetEmail}`
      };
    } catch (error) {
      console.error('Error sharing project:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all collaborators for a project
  getProjectCollaborators: async (projectId) => {
    try {
      const shareRef = ref(database, `projectShares/${projectId}`);
      const snapshot = await get(shareRef);

      if (!snapshot.exists()) {
        return { success: true, collaborators: [], pendingInvites: [] };
      }

      const data = snapshot.val();
      const collaborators = Object.entries(data.members || {}).map(([id, member]) => ({
        id,
        ...member
      }));

      const pendingInvites = Object.entries(data.pendingInvites || {}).map(([code, invite]) => ({
        code,
        ...invite
      }));

      return {
        success: true,
        collaborators,
        pendingInvites,
        owner: { id: data.ownerId, name: data.ownerName, email: data.ownerEmail }
      };
    } catch (error) {
      console.error('Error getting collaborators:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all projects shared with a user
  getSharedProjects: async (userId) => {
    try {
      const sharedRef = ref(database, `users/${userId}/sharedWithMe`);
      const snapshot = await get(sharedRef);

      if (!snapshot.exists()) {
        return { success: true, projects: [] };
      }

      const sharedData = snapshot.val();
      const projects = [];

      // Fetch actual project data for each shared project
      for (const [projectId, shareInfo] of Object.entries(sharedData)) {
        const projectRef = ref(database, `users/${shareInfo.ownerId}/projects/${projectId}`);
        const projectSnapshot = await get(projectRef);

        if (projectSnapshot.exists()) {
          projects.push({
            id: projectId,
            ...projectSnapshot.val(),
            _shareInfo: shareInfo,
            _isShared: true
          });
        }
      }

      return { success: true, projects };
    } catch (error) {
      console.error('Error getting shared projects:', error);
      return { success: false, error: error.message };
    }
  },

  // Remove a collaborator from a project
  removeCollaborator: async (ownerId, projectId, targetUserId) => {
    try {
      // Remove from projectShares
      await remove(ref(database, `projectShares/${projectId}/members/${targetUserId}`));

      // Remove from user's sharedWithMe
      await remove(ref(database, `users/${targetUserId}/sharedWithMe/${projectId}`));

      // Update member count
      const shareRef = ref(database, `projectShares/${projectId}`);
      const snapshot = await get(shareRef);
      if (snapshot.exists()) {
        const memberCount = Object.keys(snapshot.val().members || {}).length;
        await update(ref(database, `users/${ownerId}/projects/${projectId}/sharing`), {
          memberCount,
          updatedAt: serverTimestamp()
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error removing collaborator:', error);
      return { success: false, error: error.message };
    }
  },

  // Cancel a pending invite
  cancelInvite: async (projectId, inviteCode) => {
    try {
      await remove(ref(database, `projectShares/${projectId}/pendingInvites/${inviteCode}`));
      return { success: true };
    } catch (error) {
      console.error('Error canceling invite:', error);
      return { success: false, error: error.message };
    }
  },

  // Leave a shared project (for collaborators)
  leaveProject: async (userId, projectId, ownerId) => {
    try {
      // Remove from projectShares
      await remove(ref(database, `projectShares/${projectId}/members/${userId}`));

      // Remove from user's sharedWithMe
      await remove(ref(database, `users/${userId}/sharedWithMe/${projectId}`));

      return { success: true };
    } catch (error) {
      console.error('Error leaving project:', error);
      return { success: false, error: error.message };
    }
  },

  // Save user email to profile (needed for sharing lookup)
  saveUserEmail: async (userId, email) => {
    try {
      await update(ref(database, `users/${userId}/profile`), { email });
      return { success: true };
    } catch (error) {
      console.error('Error saving email:', error);
      return { success: false, error: error.message };
    }
  },

  // Create realtime listener for shared projects
  createSharedProjectsListener: (userId, callback) => {
    const sharedRef = ref(database, `users/${userId}/sharedWithMe`);
    return onValue(sharedRef, async (snapshot) => {
      if (!snapshot.exists()) {
        callback([]);
        return;
      }

      const sharedData = snapshot.val();
      const projects = [];

      for (const [projectId, shareInfo] of Object.entries(sharedData)) {
        try {
          const projectRef = ref(database, `users/${shareInfo.ownerId}/projects/${projectId}`);
          const projectSnapshot = await get(projectRef);

          if (projectSnapshot.exists()) {
            projects.push({
              id: projectId,
              ...projectSnapshot.val(),
              _shareInfo: shareInfo,
              _isShared: true,
              _ownerId: shareInfo.ownerId
            });
          }
        } catch (e) {
          console.error('Error fetching shared project:', e);
        }
      }

      callback(projects);
    });
  }
};

export default app;